import Toggle from 'react-toggle'
import { ReactNode, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faWarning } from '@fortawesome/free-solid-svg-icons'

type Props = {
  title: string
  isEnabled?: boolean | undefined
  iconPath?: string
  icon?: ReactNode
  value?: string | undefined
  placeholder?: string
  onValueUpdate?: (value: string) => void
  onEveryKeyPress?: (value: string) => void
  onToggle?: () => void
  onBeforeToggle?: () => Promise<void>
  hasError?: boolean
  rightButton?: ReactNode | null
  body?: ReactNode
  alwaysShowsBody?: boolean
  infoText?: ReactNode
  infoVariant?: 'warning' | 'error'
  withBullet?: boolean
  toggleTexts?: { on: string; off: string; loading?: string }
  isLoading?: boolean
}

export default function Setting(props: Props) {
  const { title, iconPath, value, placeholder, onValueUpdate, onToggle, onBeforeToggle, onEveryKeyPress } = props
  const { infoText, infoVariant, alwaysShowsBody, hasError, rightButton, body, icon, withBullet, toggleTexts, isLoading } = props

  const [isEnabled, setIsEnabled] = useState<boolean>(props.isEnabled ?? false)

  useEffect(() => {
    setIsEnabled(props.isEnabled ?? false)
  }, [props.isEnabled])

  const toggle = async () => {
    // If theres confirmation, wait for it and then server
    if (onBeforeToggle) {
      await onBeforeToggle()
      onToggle?.()
    } else {
      setIsEnabled(!isEnabled)
      setTimeout(() => {
        onToggle?.()
      }, 200)
    }
  }

  const explainerClassName = ['setting-explainer', infoVariant].filter(Boolean).join(' ')

  return (
    <>
      <div className="setting" title={title}>
        <div className="setting-header">
          <div className="left-side">
            <div className="setting-name">
              {(iconPath || icon) && <div className="setting-icon">{icon || <img src={iconPath} alt={`${title} icon`} />}</div>}
              <span>
                {title}
                {withBullet && isEnabled && <span className="bullet" />}
              </span>
            </div>
          </div>
          {rightButton !== null && (
            <div className="right-side">
              {rightButton ||
                (isLoading && toggleTexts?.loading ? (
                  <span>{toggleTexts.loading}</span>
                ) : (
                  <>
                    {toggleTexts && <span className="only-desktop">{isEnabled ? toggleTexts.on : toggleTexts.off}</span>}
                    <Toggle className="toggle" checked={isEnabled} icons={false} onChange={toggle} />
                  </>
                ))}
            </div>
          )}
        </div>
        {isEnabled || alwaysShowsBody ? (
          typeof body !== 'undefined' ? (
            <div className="setting-body">{body}</div>
          ) : placeholder ? (
            <div className="setting-body">
              <input
                defaultValue={value}
                onBlur={e => onValueUpdate?.(e.target.value)}
                onChange={e => onEveryKeyPress?.(e.target.value)}
                placeholder={placeholder}
                maxLength={250}
              />
              {hasError && <div className="invalid-url">URL invalid. Copiază URL-ul din browser de pe {title}.</div>}
            </div>
          ) : null
        ) : null}
      </div>
      {infoText && (
        <div className={explainerClassName}>
          <FontAwesomeIcon icon={infoVariant ? faWarning : faInfoCircle} /> {infoText}
        </div>
      )}
    </>
  )
}
